<!-- Localized -->
<template>
  <UISection
    class="w-full"
    :scrollable="false"
  >
    <template #title>
      {{ $t("pages.localization.select_language") }}
    </template>
      <div class="flex flex-col w-full gap-4">

        <Loader v-if="isLoading" />

        <template v-else>
          <CreateProfileForm
            v-if="checkPermission('localization.edit')"
            :enabled.sync="addNewMode"
            :prefill-data="profileToEdit"
            @update="onLangProfileUpdate"
            @cancel="setProfileToEdit(null)"
          />
          <LanguageList
            :list="langProfileList"
            :disabled="addNewMode || !checkPermission('localization.edit')"
            :show-flag="false"
            @clone="onLangProfileClone"
            @edit="onLangProfileEdit"
            @delete="onLangProfileDelete"
            @update:active="onActiveChange"
          />
        </template>
      </div>
      <ModalConfirm ref="confirm-popup" />
  </UISection>
</template>

<script>
// TODO handle updates from LanguageList and CreateProfileForm

import { mapGetters, mapActions } from "vuex";
import Loader from "@shared/components/WrappedLoader";
import ModalConfirm from "@shared/modal-confirm";
import {
  LanguageList,
  CreateProfileForm,
} from "./components";
import handleError from "@shared/mixins/handleError";
import { checkPermission } from "@shared/utils/functions";

export default {
  name: "language-tab",
  mixins: [handleError],
  components: {
    Loader,
    LanguageList,
    CreateProfileForm,
    ModalConfirm,
  },

  data() {
    return {
      isLoading: false,
      addNewMode: false,
      profileToEdit: null,
    };
  },

  async mounted() {
    this.isLoading = true;
    try {
      await this.fetchLangProfileList();
    } catch (error) {
      console.error("Failed to fetch languages", error)
    }
    this.isLoading = false;
  },

  computed: {
    ...mapGetters({
      getLangProfileList: "LocalizationStore/getLangProfileList",
    }),
    langProfileList() {
      return this.getLangProfileList ?? [];
    },
  },

  methods: {
    checkPermission,
    ...mapActions({
      fetchLangProfileList: "LocalizationStore/fetchLangProfileList",
      createLangProfile: "LocalizationStore/createLangProfile",
      cloneLangProfile: "LocalizationStore/cloneLangProfile",
      deleteLangProfile: "LocalizationStore/deleteLangProfile",
      updateLangProfile: "LocalizationStore/updateLangProfile",
    }),
    setAddNewMode(payload = false) {
      this.addNewMode = payload;
    },
    generateNewName(baseName) {
      let newName = baseName;
      while (this.langProfileList.some((profile) => profile.name === newName)) {
        newName = `${newName} ${String(this.$t("general.clone")).toLowerCase()}`;
      }
      return newName;
    },
    async onLangProfileClone(payload) {
      if (!payload || !checkPermission('localization.edit')) return;
      try {
        await this.cloneLangProfile({
          id: payload.language_profile_id,
          payload: {
            name: this.generateNewName(payload.name),
          }
        });
        await this.fetchLangProfileList();
        this.$toast.success(this.$t("general.create_success"));
      } catch (error) {
        this.handleError(error);
      }
    },
    async onLangProfileEdit(payload) {
      if (!payload || !checkPermission('localization.edit')) return;
      if (payload.disabled) return;
      this.setProfileToEdit(payload);
      this.setAddNewMode(true);
    },
    async onLangProfileDelete(payload) {
      if (!payload || !checkPermission('localization.edit')) return;
      if (payload.disabled) return;
      try {
        const isConfirmed = await this.$refs["confirm-popup"].show({
          message: this.$t('pages.localization.profile_delete_confirm'),
        });
        if (isConfirmed) {
          await this.deleteLangProfile(payload.language_profile_id);
          await this.fetchLangProfileList();
          this.$toast.success(this.$t("general.delete_success"));
        }
      } catch (error) {
        this.handleError(error);
      }
    },
    setProfileToEdit(payload = null) {
      if (!checkPermission('localization.edit')) return;
      this.profileToEdit = payload;
    },
    async onLangProfileUpdate(payload) {
      if (!payload || !checkPermission('localization.edit')) return;
      try {
        switch (!!this.profileToEdit) {
          case true:
            await this.updateLangProfile({
              id: this.profileToEdit.language_profile_id,
              payload: {
                active: this.profileToEdit.active,
                name: payload.name,
              },
            });
            this.$toast.success(this.$t("general.update_success"));
            break;
        
          default:
            await this.createLangProfile({
              name: payload.name,
              language_id: payload.lang?.id,
            });
            this.$toast.success(this.$t("general.create_success"));
            break;
        }
        await this.fetchLangProfileList();
        this.setProfileToEdit();
      } catch (error) {
        this.handleError(error);
      }
    },
    async onActiveChange(payload) {
      if (!payload || !checkPermission('localization.edit')) return;
      try {
        await this.updateLangProfile({
          id: payload.language_profile_id,
          payload: {
            name: payload.name, // required prop
            active: payload.active,
          },
        });
        this.$toast.success(this.$t("general.update_success"));
      } catch (error) {
        this.handleError(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
